import { authExchange, AuthUtilities } from "@urql/exchange-auth"
import { refocusExchange } from "@urql/exchange-refocus"
import {
  cacheExchange,
  CombinedError,
  createClient,
  fetchExchange,
  mapExchange,
  ssrExchange,
  subscriptionExchange,
} from "@urql/next"
import { print } from "graphql"
import { signOut } from "next-auth/react"
import { willTokenExpire } from "../../helper/sessionHelper"
import type { Session } from "../../types"
import { loadingExchange } from "./loadingExchange"

type SignOut = typeof signOut
type PromiseFn = () => Promise<Session | null>

interface LoadingCallbacks {
  incrementLoading: () => void
  decrementLoading: () => void
}

export const isAuthError = (error: CombinedError) => {
  const errorResponse = error.response as Response
  return (
    error.graphQLErrors.some(
      (e) =>
        e.extensions?.code === "UnauthorizedException" ||
        e.extensions?.code === "FORBIDDEN",
    ) || errorResponse?.status === 401
  )
}

const operationExchange = (onAuthError: SignOut) =>
  mapExchange({
    async onError(error) {
      if (isAuthError(error)) {
        if (typeof window !== "undefined") {
          await onAuthError()
        }
      }
      console.group("API Error")
      error.graphQLErrors.map((e) => {
        console.error(e.path, e.message)
        return null
      })
      console.groupEnd()
    },
  })

export const cognitoExchange = (getAuthSession: PromiseFn) =>
  authExchange(async (utilities: AuthUtilities) => {
    let session: Session | null = null
    try {
      session = (await getAuthSession()) as Session
    } catch (error) {
      console.debug("cognitoExchange error", error)
    }

    return {
      addAuthToOperation: (operation) => {
        if (!session?.token?.id_token) return operation
        return utilities.appendHeaders(operation, {
          Authorization: `Bearer ${session.token.id_token}`,
        })
      },
      willAuthError: () => {
        return willTokenExpire(session?.token?.exp as number)
      },
      didAuthError: (error) => {
        return isAuthError(error)
      },
      refreshAuth: async () => {
        session = (await getAuthSession()) as Session
      },
    }
  })
export const ssr = ssrExchange({
  isClient: typeof window === "undefined",
})

const APPSYNC_MESSAGE_TYPES = {
  CONNECTION_INIT: "connection_init",
  CONNECTION_ACK: "connection_ack",
  CONNECTION_ERROR: "connection_error",
  START: "start",
  START_ACK: "start_ack",
  STOP: "stop",
  DATA: "data",
  ERROR: "error",
  COMPLETE: "complete",
  KA: "ka",
}

class AppSyncWebSocketClient {
  private ws: WebSocket | null = null
  private subscriptions = new Map<
    string,
    (data: Record<string, unknown>) => void
  >()
  private subscriptionAcks = new Map<string, () => void>()
  private connectionPromise: Promise<void> | null = null
  private connectionResolve: (() => void) | null = null
  private keepAliveTimeout: NodeJS.Timeout | null = null

  constructor(
    private url: string,
    private apiHeader: Record<string, string>,
  ) {}

  connect(): Promise<void> {
    if (this.connectionPromise) return this.connectionPromise

    this.connectionPromise = new Promise((resolve, reject) => {
      this.connectionResolve = resolve

      this.ws = new WebSocket(this.url, ["graphql-ws"])

      this.ws.onopen = () => {
        this.ws?.send(
          JSON.stringify({ type: APPSYNC_MESSAGE_TYPES.CONNECTION_INIT }),
        )
      }

      this.ws.onmessage = (event) => {
        const message = JSON.parse(event.data)

        switch (message.type) {
          case APPSYNC_MESSAGE_TYPES.CONNECTION_ACK:
            this.startKeepAlive(message.payload?.connectionTimeoutMs || 300000)
            resolve()
            break
          case APPSYNC_MESSAGE_TYPES.KA:
            this.startKeepAlive(300000)
            break
          case APPSYNC_MESSAGE_TYPES.START_ACK: {
            const ackCallback = this.subscriptionAcks.get(message.id)
            if (ackCallback) {
              ackCallback()
              this.subscriptionAcks.delete(message.id)
            }
            break
          }
          case APPSYNC_MESSAGE_TYPES.DATA: {
            const callback = this.subscriptions.get(message.id)
            if (callback && message.payload?.data) {
              callback(message.payload)
            }
            break
          }
          case APPSYNC_MESSAGE_TYPES.ERROR:
            break
          case APPSYNC_MESSAGE_TYPES.COMPLETE:
            this.subscriptions.delete(message.id)
            break
          case APPSYNC_MESSAGE_TYPES.CONNECTION_ERROR:
            reject(
              new Error(
                message.payload?.errors?.[0]?.message || "Connection error",
              ),
            )
            break
        }
      }

      this.ws.onerror = (error) => {
        reject(error)
      }

      this.ws.onclose = () => {
        this.cleanup()
      }
    })

    return this.connectionPromise
  }

  private startKeepAlive(timeout: number) {
    if (this.keepAliveTimeout) {
      clearTimeout(this.keepAliveTimeout)
    }
    this.keepAliveTimeout = setTimeout(() => {
      this.close()
    }, timeout + 10000)
  }

  subscribe(
    id: string,
    query: string,
    variables: Record<string, unknown>,
    callback: (data: Record<string, unknown>) => void,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.subscriptions.set(id, callback)
      this.subscriptionAcks.set(id, resolve)

      const message = {
        id,
        type: APPSYNC_MESSAGE_TYPES.START,
        payload: {
          data: JSON.stringify({ query, variables }),
          extensions: { authorization: this.apiHeader },
        },
      }

      this.ws?.send(JSON.stringify(message))
    })
  }

  unsubscribe(id: string) {
    if (this.subscriptions.has(id)) {
      this.ws?.send(JSON.stringify({ id, type: APPSYNC_MESSAGE_TYPES.STOP }))
      this.subscriptions.delete(id)
    }
  }

  close() {
    this.cleanup()
    this.ws?.close()
  }

  private cleanup() {
    if (this.keepAliveTimeout) {
      clearTimeout(this.keepAliveTimeout)
    }
    this.subscriptions.clear()
    this.subscriptionAcks.clear()
    this.connectionPromise = null
    this.connectionResolve = null
  }
}

export const initClient = (
  getAuthSession: PromiseFn,
  onAuthError: SignOut,
  loadingCallbacks?: LoadingCallbacks,
) => {
  return (APP_SYNC_URL: string, WS_URL?: string) => {
    const exchanges = [
      refocusExchange(),
      cacheExchange,
      ssr,
      operationExchange(onAuthError),
      cognitoExchange(getAuthSession),
      // Add loading exchange before fetchExchange
      ...(loadingCallbacks ? [loadingExchange(loadingCallbacks)] : []),
      fetchExchange,
    ]

    if (WS_URL && typeof window !== "undefined") {
      let wsClient: AppSyncWebSocketClient | null = null

      exchanges.push(
        subscriptionExchange({
          forwardSubscription(request) {
            return {
              subscribe(sink) {
                const subscriptionId = Math.random().toString(36).substring(2)

                getAuthSession()
                  .then(async (session) => {
                    const token = session?.token?.id_token || ""
                    if (!token) {
                      throw new Error("No auth token available")
                    }

                    const url = new URL(WS_URL)
                    const host = url.host.replace(
                      "appsync-realtime-api",
                      "appsync-api",
                    )

                    const apiHeader = { host, Authorization: token }
                    const connectionUrl = `${WS_URL}?header=${btoa(JSON.stringify(apiHeader))}&payload=${btoa("{}")}`

                    wsClient = new AppSyncWebSocketClient(
                      connectionUrl,
                      apiHeader,
                    )
                    await wsClient.connect()

                    const query = request.query
                      ? typeof request.query === "string"
                        ? request.query
                        : print(request.query)
                      : ""

                    await wsClient.subscribe(
                      subscriptionId,
                      query,
                      request.variables || {},
                      (data) => sink.next(data),
                    )
                    sink.next({ data: { __subscriptionReady: true } })
                  })
                  .catch((error) => sink.error(error))

                return {
                  unsubscribe: () => {
                    wsClient?.unsubscribe(subscriptionId)
                    wsClient?.close()
                  },
                }
              },
            }
          },
        }),
      )
    }

    return createClient({
      url: APP_SYNC_URL,
      exchanges,
      suspense: true,
    })
  }
}
